import React, { useState, useRef } from 'react';
import {
  TouchableOpacity,
  View,
  Text,
  StyleSheet,
  TextInput,
  Animated,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { goBack, navigate } from '../../navigation/RootNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { removeAuthToken } from '../../redux/action/authActions';
import { COLORS, LAYOUT, BORDER_RADIUS } from '../../constants/theme';

/* ── Initials helper ─────────────────────────────────── */
const getInitials = (firstName = '', lastName = '') => {
  const f = (firstName || '').trim();
  const l = (lastName || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f.substring(0, 2).toUpperCase();
  return 'U';
};

/* ── Custom Logout Modal (tablet) ────────────────────── */
const LogoutModal = ({ visible, onConfirm, onCancel }) => {
  if (!visible) return null;
  return (
    <View style={lm.portalRoot}>
      {/* Backdrop */}
      <TouchableOpacity
        style={StyleSheet.absoluteFillObject}
        activeOpacity={1}
        onPress={onCancel}
      />

      {/* Card */}
      <View style={lm.card}>
        {/* Icon */}
        <View style={lm.iconWrap}>
          <Ionicons name="log-out-outline" size={28} color="#ef4444" />
        </View>

        <Text style={lm.title}>Log out?</Text>
        <Text style={lm.subtitle}>
          You'll be signed out of your account.{'\n'}You can log back in anytime.
        </Text>

        {/* Actions */}
        <TouchableOpacity style={lm.logoutBtn} onPress={onConfirm} activeOpacity={0.85}>
          <Ionicons name="log-out-outline" size={16} color="#fff" style={{ marginRight: 6 }} />
          <Text style={lm.logoutBtnText}>Yes, Log Out</Text>
        </TouchableOpacity>

        <TouchableOpacity style={lm.cancelBtn} onPress={onCancel} activeOpacity={0.75}>
          <Text style={lm.cancelBtnText}>Stay Signed In</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const lm = StyleSheet.create({
  portalRoot: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 99999,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: 320,
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 28,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.18,
    shadowRadius: 24,
    elevation: 20,
    zIndex: 100000,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#fee2e2',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1a1a2e',
    marginBottom: 8,
    fontFamily: 'poppins',
  },
  subtitle: {
    fontSize: 13,
    color: '#888',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
    fontFamily: 'poppins',
  },
  logoutBtn: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ef4444',
    borderRadius: 30,
    paddingVertical: 13,
    marginBottom: 10,
  },
  logoutBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
    fontFamily: 'poppins',
  },
  cancelBtn: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 30,
    borderWidth: 1.5,
    borderColor: '#e5e7eb',
  },
  cancelBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#555',
    fontFamily: 'poppins',
  },
});

/* ── Main Tablet StatusBar ───────────────────────────── */
const StatusBarTab = ({ backbutton, title, backNavigation }) => {
  const userToken   = useSelector((state) => state?.AppReducer?.token);
  const userProfile = useSelector((state) => state?.Login?.userDetails);
  const dispatch    = useDispatch();

  const [logoutVisible, setLogoutVisible] = useState(false);

  const firstName = userProfile?.first_name || '';
  const lastName  = userProfile?.last_name  || '';
  const initials  = getInitials(firstName, lastName);

  const handleLogout = () => {
    setLogoutVisible(false);
    dispatch(removeAuthToken());
    navigate('login');
  };

  return (
    <>
      <View style={styles.container}>
        {/* Left side: back button */}
        <View style={styles.leftSection}>
          <TouchableOpacity
            style={styles.backBtn}
            onPress={() => backNavigation ? navigate(backNavigation) : goBack()}
            activeOpacity={0.7}
          >
            {backbutton ? (
              <>
                <Ionicons name="chevron-back" size={20} color={COLORS.primary} />
                <Text style={styles.backText}>
                  {backNavigation === 'CurrentOnboards' ? 'Back To Current Tenancies' : 'Back'}
                </Text>
              </>
            ) : null}
          </TouchableOpacity>
        </View>

        {/* Right side: avatar */}
        {userToken ? (
          <View style={styles.right}>
            {/* Avatar */}
            <TouchableOpacity
              style={styles.avatar}
              onPress={() => setLogoutVisible(true)}
              activeOpacity={0.8}
            >
              <Text style={styles.avatarText}>{initials}</Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      {/* Logout confirmation modal */}
      <LogoutModal
        visible={logoutVisible}
        onConfirm={handleLogout}
        onCancel={() => setLogoutVisible(false)}
      />
    </>
  );
};

/* ── Styles ──────────────────────────────────────────── */
const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: LAYOUT.sidebarWidthTab,
    right: 0,
    height: 52,
    backgroundColor: COLORS.surface,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    zIndex: 9,
  },
  leftSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backText: {
    color: COLORS.primary,
    fontSize: 13,
    fontWeight: '600',
    marginLeft: 2,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.surfaceHover || '#f5f5f9',
    borderRadius: BORDER_RADIUS.lg || 10,
    borderWidth: 1,
    borderColor: COLORS.primary,
    paddingHorizontal: 10,
    paddingVertical: Platform.OS === 'web' ? 5 : 7,
    maxWidth: 320,
    overflow: 'hidden',
    marginLeft: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: COLORS.textPrimary,
    paddingVertical: 0,
  },
  right: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: COLORS.surfaceHover,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: COLORS.border,
  },
  iconBtnActive: {
    backgroundColor: '#f0e8ff',
    borderColor: COLORS.primary,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: 0.5,
    fontFamily: 'poppins',
  },
});

export default StatusBarTab;
